import { useState, type CSSProperties, type ReactNode } from "react";
import { useShallow } from "zustand/react/shallow";
import { isAiResidentActorId } from "@townsy/contracts";
import { errorMessage } from "../../api/client";
import { flagLabel } from "../../lib/flag";
import { muteActor, unmuteActor } from "../../safety/mutes";
import { selectCardPerson, selectIsGuest, useTownStore, type ActorSummary } from "../../state/store";
import { FlagIcon } from "../FlagIcon";
import { JoinFriendButton } from "../worlds/JoinFriend";

const AI_BADGE: CSSProperties = {
  display: "inline-block",
  marginLeft: 6,
  padding: "0 7px",
  borderRadius: "var(--radius-pill)",
  background: "var(--color-action)",
  color: "#fff",
  fontSize: "0.72rem",
  fontWeight: 800,
  lineHeight: "1.4",
  letterSpacing: "0.04em",
  verticalAlign: "2px",
};

/** The text "Say hi" puts in the chat box: addressed to the person by name (public room chat). */
export function sayHiDraft(label: string): string {
  return `@${label} `;
}

/**
 * A person's card (founder request 2026-10-13; ADR-0013), opened by clicking them or their name tag: who they are (flag
 * or the AI badge), Say hi (opens room chat addressed to them; an AI resident within about 20 m stops and answers),
 * Mute / Unmute (hides their chat for you), and Report (to Townsy moderators). Chat stays public room chat: Townsy has
 * no private messages in v1 (docs/00 R09).
 */
export function PersonCard() {
  const { id, actor, away, muted, roomJoined } = useTownStore(
    useShallow((s) => {
      const selected = s.ui.selectedActorId;
      // ADR-0043: a person who walked out of this tab's view on the street keeps their card (marked "not nearby").
      const person = selectCardPerson(s);
      return { id: selected, actor: person?.actor, away: person?.away ?? false, muted: selected ? selected in s.mutes : false, roomJoined: s.room.status === "joined" };
    }),
  );
  const isGuest = useTownStore(selectIsGuest);
  const dmOn = useTownStore((s) => Boolean(s.bootstrap?.features.privateMessagesEnabled));
  const dm: PersonCardViewProps["messages"] = !dmOn ? "off" : isGuest ? "sign_in" : "on";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!id || !actor || actor.isLocal) return null;
  const S = useTownStore.getState;
  const close = () => S().selectActor(null);
  return (
    <PersonCardView
      actor={actor}
      away={away}
      muted={muted}
      roomJoined={roomJoined}
      isGuest={isGuest}
      busy={busy}
      error={error}
      onClose={close}
      onSayHi={() => {
        S().setChatDraft(sayHiDraft(actor.label));
        S().toggleChat(true);
        close();
      }}
      onToggleMute={async () => {
        setBusy(true);
        setError(null);
        try {
          if (muted) await unmuteActor(actor.actorId, !isGuest);
          else await muteActor(actor.actorId, !isGuest);
          S().pushToast(muted ? `${actor.label} is unmuted. You see their messages again.` : `${actor.label} is muted. You won't see their messages.`, "info");
        } catch (err) {
          setError(errorMessage(err, "That did not work. Try again."));
        } finally {
          setBusy(false);
        }
      }}
      onReport={() => S().openPersonReport({ actorId: actor.actorId, label: actor.label })}
      messages={dm}
      onMessage={() => {
        S().openMessages({ recipientActorId: actor.actorId });
        close();
      }}
      join={<JoinFriendButton actorId={actor.actorId} />}
    />
  );
}

export interface PersonCardViewProps {
  actor: ActorSummary;
  /**
   * ADR-0043: on the street the person is no longer in this tab's view (walked out of range, went inside or left; the tab
   * cannot tell which). The card stays: Say hi (street chat is town-wide), Message, Mute and Report work by actor id.
   */
  away?: boolean;
  muted: boolean;
  roomJoined: boolean;
  isGuest: boolean;
  busy?: boolean;
  error?: string | null;
  onClose(): void;
  onSayHi(): void;
  onToggleMute(): Promise<void> | void;
  onReport(): void;
  /** Private messages (ADR-0014): off on this server, the viewer must sign in, or available. */
  messages?: "off" | "sign_in" | "on";
  onMessage?(): void;
  /**
   * ADR-0043: "Join them in Finch" (shown only when the server says which world this person is in; inside a building
   * people from every world meet, so it can name another copy). Absent: nothing is shown.
   */
  join?: ReactNode;
}

export function PersonCardView({ actor, away = false, muted, roomJoined, isGuest, busy = false, error = null, onClose, onSayHi, onToggleMute, onReport, messages = "off", onMessage, join }: PersonCardViewProps) {
  const ai = isAiResidentActorId(actor.actorId);
  const messageBlocked = ai ? "AI residents don't take private messages." : messages === "sign_in" ? "Sign in to send private messages." : null;
  return (
    <section className="person-card" role="region" aria-labelledby="person-card-name" data-testid="person-card" data-actor-id={actor.actorId} data-movement-block="true">
      <div className="person-card-head">
        <h2 id="person-card-name" className="person-card-name">
          {ai ? null : (
            <span title={flagLabel(actor.countryCode)}>
              <FlagIcon code={actor.countryCode} />{" "}
            </span>
          )}
          {actor.label}
          {ai ? (
            <span style={AI_BADGE} title="AI resident: a computer-run townsperson, not a real visitor" aria-label="AI resident">
              AI
            </span>
          ) : null}
        </h2>
        <button type="button" className="btn btn-ghost icon-btn" aria-label="Close" onClick={onClose} data-testid="person-card-close">
          ×
        </button>
      </div>
      <p className="small muted" style={{ margin: 0 }}>
        {ai
          ? "AI resident: a computer-run townsperson. Say hi and they stop and answer if you are within about 20 m."
          : "Say hi starts a message to them in room chat (everyone here can read it)."}
        {muted ? " You muted them: their messages are hidden for you." : ""}
      </p>
      {away ? (
        <p className="small muted" style={{ margin: 0 }} data-testid="person-away">
          Not nearby right now: they are out of sight (further along the street, inside a building, or gone).
        </p>
      ) : null}
      {error ? (
        <div className="small error-text" role="alert">
          {error}
        </div>
      ) : null}
      <div className="row person-card-actions">
        <button type="button" className="btn btn-primary btn-sm" onClick={onSayHi} disabled={!roomJoined} data-testid="person-say-hi">
          Say hi
        </button>
        {messages !== "off" ? (
          <button type="button" className="btn btn-sm" onClick={onMessage} disabled={Boolean(messageBlocked)} title={messageBlocked ?? "Send a private message"} data-testid="person-message">
            Message
          </button>
        ) : null}
        <button type="button" className="btn btn-sm" onClick={() => void onToggleMute()} disabled={busy} aria-pressed={muted} data-testid="person-mute">
          {busy ? "Saving…" : muted ? "Unmute" : "Mute"}
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={onReport} data-testid="person-report">
          Report…
        </button>
      </div>
      {join && !ai ? join : null}
      {messages !== "off" && messageBlocked ? (
        <p className="hint" style={{ margin: 0 }} data-testid="person-message-hint">
          {messageBlocked}
        </p>
      ) : null}
      {isGuest && !muted ? <p className="hint" style={{ margin: 0 }}>As a guest, mutes stay in this browser. Sign in to keep them on your account.</p> : null}
    </section>
  );
}
